import React, { useState, useRef } from "react";
import { api, formatApiError, fmtINR, fmtDate } from "../lib/apiClient";
import { X, Robot, UploadSimple, Check, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

// Two-step AI re-read: pick source (attached / corrected upload) → review before/after diff → apply (by section) or reject.
export function RescanBillModal({ bill, onClose, onApplied }) {
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [accept, setAccept] = useState({ items: true, amounts: true, header: true });
  const fileRef = useRef(null);

  const propose = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      const { data } = await api.post(`/inward-bills/${bill.id}/rescan`, fd, { timeout: 120000, headers: { "Content-Type": "multipart/form-data" } });
      setProposal(data);
      (data.warnings || []).slice(0, 3).forEach(w => toast.warning(w));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inward-bills/${bill.id}/rescan/apply`, { proposal_id: proposal.id, accept_items: accept.items, accept_amounts: accept.amounts, accept_header: accept.header });
      toast.success(`Bill ${bill.bill_no} updated · ${data.items_count} item${data.items_count !== 1 ? "s" : ""}`);
      onApplied(data.bill);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-[60] flex items-center justify-center p-2 sm:p-4" onClick={busy ? undefined : onClose} data-testid="rescan-modal">
      <div className={`bg-white rounded-lg w-full ${proposal ? "max-w-5xl" : "max-w-md"} shadow-2xl max-h-[94vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-white z-10">
          <div>
            <div className="font-display font-semibold inline-flex items-center gap-2"><Robot size={18} className="text-brand" /> {proposal ? "Review AI changes" : "Re-read bill with AI"}</div>
            <div className="text-xs text-ink-muted">Bill <b className="font-mono">{bill.bill_no}</b> · {bill.vendor_name}{proposal?.confidence != null && <span className="ml-2 status-pill status-info">confidence {proposal.confidence}%</span>}</div>
          </div>
          <button onClick={onClose} disabled={busy} className="btn-ghost" data-testid="rescan-close"><X size={16} /></button>
        </div>

        {!proposal && (
          <div className="p-5 space-y-3">
            <div className="text-xs text-ink-muted">AI will read the file and propose new items, taxable value and GST. Nothing is saved until you review and accept.</div>
            <button onClick={() => propose(null)} disabled={busy || !bill.file_id} className="btn-primary w-full inline-flex items-center justify-center gap-1.5" data-testid="rescan-existing">
              <Robot size={14} /> {busy ? "Reading with AI…" : bill.file_id ? "Re-read the attached file" : "No file attached to this bill"}
            </button>
            <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-secondary w-full inline-flex items-center justify-center gap-1.5" data-testid="rescan-upload">
              <UploadSimple size={14} /> Upload a corrected file (PDF / photo) & re-read
            </button>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="rescan-file-input"
              onChange={e => { const f = e.target.files?.[0]; if (f) propose(f); e.target.value = ""; }} />
          </div>
        )}

        {proposal && (
          <div className="p-5 space-y-5 text-sm">
            <Section title="Bill header" testId="diff-header" checked={accept.header} onToggle={() => setAccept(a => ({ ...a, header: !a.header }))}>
              <FieldDiff label="Vendor" before={proposal.before.vendor_name} after={proposal.after.vendor_name} hint={proposal.ai_vendor_name && proposal.ai_vendor_name !== proposal.after.vendor_name ? `AI read: ${proposal.ai_vendor_name}` : ""} />
              <FieldDiff label="GSTIN" before={proposal.before.vendor_gstin} after={proposal.after.vendor_gstin} mono />
              <FieldDiff label="Bill No" before={proposal.before.bill_no} after={proposal.after.bill_no} mono hint={proposal.ai_bill_no && proposal.ai_bill_no !== proposal.after.bill_no ? `AI read: ${proposal.ai_bill_no}` : ""} />
              <FieldDiff label="Bill Date" before={fmtDate(proposal.before.bill_date)} after={fmtDate(proposal.after.bill_date)} hint={proposal.ai_bill_date && proposal.ai_bill_date !== proposal.after.bill_date ? `AI read: ${fmtDate(proposal.ai_bill_date)}` : ""} />
              <FieldDiff label="Purchase type" before={proposal.before.purchase_type} after={proposal.after.purchase_type} />
            </Section>
            <Section title="Amounts" testId="diff-amounts" checked={accept.amounts} onToggle={() => setAccept(a => ({ ...a, amounts: !a.amounts }))}>
              {["taxable", "cgst", "sgst", "igst", "cess", "total"].map(k => (
                <FieldDiff key={k} label={k === "total" ? "Bill Total" : k.toUpperCase()} before={fmtINR(proposal.before[k])} after={fmtINR(proposal.after[k])} bold={k === "total"} />
              ))}
            </Section>
            <Section title={`Items (${proposal.before.items.length} → ${proposal.after.items.length})`} testId="diff-items" checked={accept.items} onToggle={() => setAccept(a => ({ ...a, items: !a.items }))}>
              <ItemsDiff before={proposal.before.items} after={proposal.after.items} />
            </Section>
            <div className="flex items-center justify-between gap-2 flex-wrap pt-2 border-t border-border">
              <div className="text-xs text-ink-muted">Untick a section to keep the current values for it.</div>
              <div className="flex gap-2">
                <button onClick={onClose} disabled={busy} className="btn-secondary" data-testid="rescan-reject">Reject changes</button>
                <button onClick={apply} disabled={busy || !(accept.items || accept.amounts || accept.header)} className="btn-primary inline-flex items-center gap-1.5" data-testid="rescan-accept"><Check size={14} weight="bold" /> {busy ? "Applying…" : "Accept & update bill"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, checked, onToggle, testId, children }) {
  return (
    <div className={`rounded-md border ${checked ? "border-border" : "border-dashed border-border opacity-60"} p-3 space-y-2`} data-testid={testId}>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={checked} onChange={onToggle} data-testid={`${testId}-accept`} />
        <span className="label-overline">{title}</span>
        <span className="text-[10px] text-ink-muted ml-auto">{checked ? "will be updated" : "kept as is"}</span>
      </label>
      {children}
    </div>
  );
}

function FieldDiff({ label, before, after, mono, bold, hint }) {
  const changed = String(before ?? "") !== String(after ?? "");
  return (
    <div className={`grid grid-cols-[110px_1fr_16px_1fr] items-center gap-2 text-xs ${changed ? "bg-amber-50 rounded px-1 py-0.5" : ""}`} data-changed={changed}>
      <div className="text-ink-muted">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${changed ? "line-through text-ink-muted" : ""}`}>{before || "—"}</div>
      <ArrowRight size={12} className={changed ? "text-amber-600" : "text-ink-muted/40"} />
      <div className={`${mono ? "font-mono" : ""} ${bold ? "font-bold" : ""} ${changed ? "text-emerald-700 font-semibold" : ""}`}>{after || "—"}{hint && <div className="text-[10px] text-ink-muted font-normal">{hint}</div>}</div>
    </div>
  );
}

const itemKey = (it) => `${(it.name || "").trim().toLowerCase()}|${(it.hsn || "").trim()}`;

function ItemsDiff({ before, after }) {
  const bMap = new Map(before.map(it => [itemKey(it), it]));
  const aMap = new Map(after.map(it => [itemKey(it), it]));
  const rows = [];
  before.forEach(b => {
    const a = aMap.get(itemKey(b));
    if (!a) rows.push({ kind: "removed", b });
    else rows.push({ kind: (b.qty !== a.qty || b.rate !== a.rate || b.gst_rate !== a.gst_rate || b.amount !== a.amount) ? "changed" : "same", b, a });
  });
  after.forEach(a => { if (!bMap.has(itemKey(a))) rows.push({ kind: "added", a }); });
  const cls = { removed: "bg-red-50", added: "bg-emerald-50", changed: "bg-amber-50", same: "" };
  const pill = { removed: "status-error", added: "status-success", changed: "status-warning", same: "status-neutral" };
  const cell = (b, a, fmt = (x) => x) => (b === undefined ? fmt(a) : a === undefined ? fmt(b) : b === a ? fmt(a)
    : <span><span className="line-through text-ink-muted mr-1">{fmt(b)}</span><span className="font-semibold text-emerald-700">{fmt(a)}</span></span>);
  return (
    <div className="table-wrapper">
      <table className="table-base text-xs" data-testid="diff-items-table">
        <thead><tr><th>Change</th><th>Item</th><th>HSN</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">GST%</th><th className="text-right">Amount</th></tr></thead>
        <tbody>
          {rows.map((r, i) => {
            const it = r.a || r.b;
            return (
              <tr key={`${r.kind}-${it.name}-${i}`} className={cls[r.kind]} data-testid={`diff-item-${r.kind}`}>
                <td><span className={`status-pill ${pill[r.kind]}`}>{r.kind}</span></td>
                <td className={r.kind === "removed" ? "line-through text-ink-muted" : "font-semibold"}>{it.name}</td>
                <td className="font-mono">{it.hsn || "—"}</td>
                <td className="text-right tabular">{cell(r.b?.qty, r.a?.qty)}</td>
                <td className="text-right tabular">{cell(r.b?.rate, r.a?.rate, fmtINR)}</td>
                <td className="text-right tabular">{cell(r.b?.gst_rate, r.a?.gst_rate, x => `${x}%`)}</td>
                <td className="text-right tabular">{cell(r.b?.amount, r.a?.amount, fmtINR)}</td>
              </tr>
            );
          })}
          {rows.length === 0 && <tr><td colSpan={7} className="text-center py-4 text-ink-muted">No items before or after.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
